import { createSelector } from 'reselect';
import { RootState } from '../../../types';
import { EntityTypes } from '../../../constants/entities';
import { IOrganisationConfig, IOrganisationTag, ITheme } from 'types/domain/organisation';
import { IKeyArray } from 'types/IKeyArray';
import { GenericStatus } from 'types/domain/base';
import { IEntityReducer } from 'core/reducers/entities';
import { getEntities } from '../utils/getEntities';
import { IVariable, IVariableDAO, IVariableOption, IVariableReport } from 'types/domain/variable';
import { ITagJoin } from 'types/domain/tag';
import { IModule } from 'types/domain/module';
import { IOrgUserVariableDAO } from 'types/domain/user';
import { parseUserDAO } from './users';

const entity = (state: RootState) => state.entity.present;
const moduleId = (state: RootState, id?: number) => id;
const courseId = (state: RootState) => state.state[EntityTypes.CERTIFICATE];

export const selectVariables = createSelector(
  [entity, moduleId, courseId],
  (entity: IEntityReducer, moduleId, courseId) => {
    return (
      getEntities(entity, EntityTypes.VARIABLE) //, moduleId ? 'moduleId' : null, moduleId)
        //.filter(m => (courseId ? m.moduleId === null : true))
        .filter(v => v.status > -1)
        .map((variable: IVariable) => {
          return {
            ...variable,
            name: moduleVariableName(entity, variable) || variable.name,
            image: moduleVariableImage(entity, variable),
            moduleId: variableModuleId(entity, variable),
            text: variable.textId ? entity[EntityTypes.TEXT][variable.textId] : null,
            tags: getEntities(entity, EntityTypes.TAG_JOIN, 'variableId', variable.id) as ITagJoin[],
            options: getEntities(entity, EntityTypes.VARIABLE_OPTION, 'variableId', variable.id) as IVariableOption[],
          } as IVariableDAO;
        })
    );
  },
);

export const selectVariableReports = createSelector(
  [entity, moduleId, courseId],
  (entity: IEntityReducer, moduleId, courseId) => {
    return (
      getEntities(entity, EntityTypes.VARIABLE) //, moduleId ? 'moduleId' : null, moduleId)
        //.filter(m => (courseId ? m.moduleId === null : true))
        .filter(v => v.status > -1)
        .map((variable: IVariable) => {
          return {
            ...variable,
            name: moduleVariableName(entity, variable) || variable.name,
            image: moduleVariableImage(entity, variable),
            moduleId: variableModuleId(entity, variable),
            text: variable.textId ? entity[EntityTypes.TEXT][variable.textId] : null,
            tags: getEntities(entity, EntityTypes.TAG_JOIN, 'variableId', variable.id) as ITagJoin[],
            options: getEntities(entity, EntityTypes.VARIABLE_OPTION, 'variableId', variable.id) as IVariableOption[],
            organisationUserVariables: getEntities(
              entity,
              EntityTypes.ORG_USER_VARIABLE,
              'variableId',
              variable.id,
            ).map(orv => {
              return {
                ...orv,
                variable,
                option: orv.variableOptionId ? entity[EntityTypes.VARIABLE_OPTION][orv.variableOptionId] : null,
                organisationUser: parseUserDAO(entity, orv.organisationUserId),
              } as IOrgUserVariableDAO;
            }),
          } as IVariableReport;
        })
    );
  },
);

export const moduleVariableName = (entity: IEntityReducer, variable) => {
  if (variable.textId && entity[EntityTypes.TEXT][variable.textId]?.shortText)
    return entity[EntityTypes.TEXT][variable.textId]?.shortText;

  if (variable.moduleId)
    return entity[EntityTypes.TEXT][entity[EntityTypes.MODULE][variable.moduleId]?.textId]?.shortText;

  const module = Object.values(entity[EntityTypes.MODULE]).find((mod: IModule) => mod.variableId === variable.id);

  if (module) return entity[EntityTypes.TEXT][module.textId]?.shortText || module.name;
  else return variable.name;
};

export const moduleVariableImage = (entity: IEntityReducer, variable) => {
  if (variable.moduleId) return entity[EntityTypes.IMAGE][entity[EntityTypes.MODULE][variable.moduleId]?.imageId];

  const module = Object.values(entity[EntityTypes.MODULE]).find((mod: IModule) => mod.variableId === variable.id);

  if (module) return entity[EntityTypes.IMAGE][module.imageId];
  else return variable.name;
};

export const variableModuleId = (entity: IEntityReducer, variable) => {
  if (variable.moduleId) return variable.moduleId;

  const module = Object.values(entity[EntityTypes.MODULE]).find((mod: IModule) => mod.variableId === variable.id);

  if (module) return module.id;
};
